/**
 * Shapes and conventions shared by every endpoint — ADR-001 §11.1 and §11.2.
 *
 * Rules encoded here (do not re-implement them per endpoint):
 *  - Dates are `YYYY-MM-DD`; timestamps are RFC 3339 **with an offset**.
 *  - Money is a number in INR lakh and the field name ends in `_lakh`.
 *  - Percentages are numbers 0–100.
 *  - Lists are cursor-paged: `{ data, next_cursor }`, `?limit` default 50, max 200. No offset paging.
 *  - Errors are always `{ error: { code, message, field_errors? } }`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------
export const Uuid = z.uuid();
/** Business date, `YYYY-MM-DD`, interpreted in Asia/Kolkata. */
export const IsoDate = z.iso.date();
/** RFC 3339 timestamp with an offset, e.g. `2026-10-16T15:45:00+05:30`. */
export const Timestamp = z.iso.datetime({ offset: true });
/** Money in INR lakh. Field names carrying it always end in `_lakh`. */
export const Lakh = z.number().nonnegative().meta({ description: 'Amount in INR lakh' });
/** Percentage, 0–100, displayed without decimals. */
export const Percent = z.number().min(0).max(100);
/** Optimistic-concurrency version; bumped on every write. Sent back as `If-Match`. */
export const Version = z.number().int().positive().meta({
    description: 'Row version, bumped on every write. Send it back in If-Match on PATCH/PUT.',
});
// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------
export const ERROR_CODES = [
    'no_session',
    'invalid_credentials',
    'not_provisioned',
    'must_change_password',
    'forbidden',
    'not_found',
    'validation_failed',
    'conflict',
    'precondition_failed',
    'rate_limited',
    'upstream_unavailable',
    'internal_error',
];
export const ErrorCode = z.enum(ERROR_CODES).meta({ id: 'ErrorCode' });
/** The HTTP status each error code is always sent with. */
export const ERROR_STATUS = {
    no_session: 401,
    invalid_credentials: 401,
    not_provisioned: 403,
    must_change_password: 403,
    forbidden: 403,
    not_found: 404,
    validation_failed: 422,
    conflict: 409,
    precondition_failed: 412,
    rate_limited: 429,
    upstream_unavailable: 503,
    internal_error: 500,
};
export const FieldError = z
    .object({
    /** Dotted path of the offending field in the request, e.g. `due` or `contacts.0.email`. */
    path: z.string().min(1),
    /** Plain English, says how to fix it (guide §9). */
    message: z.string().min(1),
})
    .meta({ id: 'FieldError' });
export const ErrorBody = z
    .object({
    error: z.object({
        code: ErrorCode,
        /** Plain English: what went wrong and how to fix it. Safe to show to the user. */
        message: z.string().min(1),
        field_errors: z.array(FieldError).optional(),
    }),
})
    .meta({ id: 'ErrorBody' });
/**
 * `412 precondition_failed` body (ADR §11.1): the standard error plus `current`, the resource as it is now,
 * so the client can show the latest and retry with its `version`. Per route, `current` has the route's
 * `preconditionCurrent` (or success) schema — see `preconditionFailedBody`.
 */
export const preconditionFailedBody = (current) => ErrorBody.extend({ current });
// ---------------------------------------------------------------------------
// Paging
// ---------------------------------------------------------------------------
export const PAGE_LIMIT_DEFAULT = 50;
export const PAGE_LIMIT_MAX = 200;
/** `?limit&cursor` — every list endpoint accepts exactly these for paging. */
export const PageQuery = z.object({
    limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(PAGE_LIMIT_DEFAULT),
    /** Opaque; pass back the `next_cursor` from the previous page unchanged. */
    cursor: z.string().min(1).max(512).optional(),
});
export const page = (item) => z.object({
    data: z.array(item),
    /** `null` on the last page. */
    next_cursor: z.string().nullable(),
});
/** A short, unpaged list (contacts, email domains, SPOC history). */
export const list = (item) => z.object({ data: z.array(item) });
// ---------------------------------------------------------------------------
// Derived values — every computed number carries its explanation (§11.1)
// ---------------------------------------------------------------------------
export const ReasonBand = z.enum(['ok', 'warn', 'bad']);
export const Reason = z
    .object({
    /** Stable machine key of the rule that fired, e.g. `util_watch`. */
    signal: z.string().min(1),
    /** Points applied (negative for a penalty, positive for a bonus). Absent when the reason is informational. */
    penalty: z.number().optional(),
    /** The plain-English line printed under the number. */
    detail: z.string().min(1),
    band: ReasonBand,
    /**
     * `mail` marks a reason derived from emails (the "From emails" chip). It hides nothing by itself: which
     * reasons a caller may see is decided by `signal` — see TONE_REASON_SIGNALS.
     */
    source: z.literal('mail').optional(),
})
    .meta({ id: 'Reason' });
/**
 * The one masking rule for tone (ADR §8.1/§8.5, operator scope decision): reasons whose `signal` is one of these
 * are for callers with `toneReason.read` only; everyone else gets the score (tone points included) without them.
 * Other email-derived reasons (`source: 'mail'`, e.g. waiting for our reply) stay visible to all.
 */
export const TONE_REASON_SIGNALS = ['tone', 'positive_tone'];
/** A reason as a caller without `toneReason.read` receives it: never a tone signal (a leak fails validation).
 * Written as a pattern, not a refine, so the rule is in the published OpenAPI schema too. */
export const MemberReason = Reason.extend({
    signal: z
        .string()
        .min(1)
        .regex(new RegExp(`^(?!(?:${TONE_REASON_SIGNALS.join('|')})$)`), {
        message: 'Tone reasons are only for callers with toneReason.read',
    }),
}).meta({ id: 'MemberReason' });
export const derived = (value) => z.object({
    value,
    /** The snapshot date this value was computed for. */
    as_of: IsoDate,
    explain: z.array(Reason),
});
/** `derived` for a caller without `toneReason.read`: the same, with tone reasons refused. */
export const derivedMember = (value) => z.object({ value, as_of: IsoDate, explain: z.array(MemberReason) });
// ---------------------------------------------------------------------------
// Request headers
// ---------------------------------------------------------------------------
/** `Idempotency-Key` — accepted on every creating POST; a UUID, kept 24 h (§11.1). */
export const IdempotencyHeaders = z.object({
    'idempotency-key': z.uuid().optional(),
});
/** `If-Match` — required on every PATCH/PUT of a versioned resource. Value is the `version` (optionally quoted). */
export const IfMatchHeaders = z.object({
    'if-match': z
        .string()
        .regex(/^(W\/)?"?\d+"?$/, 'Send the version you last read, e.g. If-Match: "3"'),
});
/** `If-Match` that may be omitted (the action is safe without it, but honours it when sent). */
export const OptionalIfMatchHeaders = z.object({
    'if-match': IfMatchHeaders.shape['if-match'].optional(),
});
//# sourceMappingURL=common.js.map